/**
 * Quan hệ phụ thuộc công đoạn → máy / người thực hiện (03/11/2026).
 * Nhóm công đoạn (process_groups.code) quyết định loại máy (machines.type) và tổ (users.section) được chọn.
 * Dùng chung: ô chọn máy / người (combobox) và kiểm tra khi lưu nhật ký sản xuất.
 * Nhóm chưa khai báo ở đây → null = không giới hạn (không chặn dữ liệu đang thiếu).
 */

export interface ProcessGroupResources {
  /** Loại máy được chọn; null = không giới hạn */
  machineTypes: string[] | null;
  /** Tổ / bộ phận (users.section) được chọn; null = không giới hạn */
  sections: string[] | null;
  /** Phòng (users.department) được chọn thêm ngoài tổ (VD nhân sự QC chưa ghi tổ); null = không xét */
  departments: string[] | null;
}

const MAP: Record<string, { machineTypes: string[]; sections: string[]; departments?: string[] }> = {
  PREPRESS: { machineTypes: ['OTHER'], sections: ['Thiết kế', 'CTP', 'Chế bản', 'Bình file'] },
  PRINTING: { machineTypes: ['OFFSET_PRINTER', 'FLEXO_PRINTER', 'SILK_SCREEN'], sections: ['In', 'In lưới', 'Chuyên gia Công nghệ In'] },
  COATING: { machineTypes: ['COATER', 'LAMINATOR', 'SILK_SCREEN'], sections: ['Cán màng', 'In lưới', 'In'] },
  UV_COATING: { machineTypes: ['COATER', 'SILK_SCREEN', 'LAMINATOR'], sections: ['Cán màng', 'In lưới', 'In'] },
  LAMINATION: { machineTypes: ['LAMINATOR'], sections: ['Cán màng'] },
  MOUNTING: { machineTypes: ['LAMINATOR', 'OTHER'], sections: ['Cán màng', 'Dán', 'Bồi'] },
  CUTTING: { machineTypes: ['PAPER_CUTTER'], sections: ['Xén', 'Cắt'] },
  DIE_CUTTING: { machineTypes: ['DIE_CUTTER', 'HOT_FOIL'], sections: ['Bế', 'Ép nhũ'] },
  FOIL_STAMPING: { machineTypes: ['HOT_FOIL', 'DIE_CUTTER'], sections: ['Ép nhũ', 'Bế'] },
  EMBOSSING: { machineTypes: ['DIE_CUTTER', 'HOT_FOIL'], sections: ['Bế', 'Ép nhũ'] },
  GLUING: { machineTypes: ['FOLDER_GLUER', 'WINDOW_PATCHER'], sections: ['Dán'] },
  FINISHING: { machineTypes: ['FOLDER_GLUER', 'WINDOW_PATCHER', 'DIE_CUTTER', 'PACKING'], sections: ['Dán', 'Đóng gói', 'Bế'] },
  QC: { machineTypes: ['PACKING'], sections: ['PQC', 'OQC', 'QC', 'QA'], departments: ['QC', 'Kiểm Soát Chất Lượng'] },
  PACKING: { machineTypes: ['PACKING'], sections: ['Đóng gói'] },
};

const norm = (s: unknown) =>
  String(s ?? '')
    .normalize('NFC')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

export function resourcesForProcessGroup(code: string | null | undefined): ProcessGroupResources {
  const hit = code ? MAP[String(code).trim().toUpperCase()] : undefined;
  return hit
    ? { machineTypes: [...hit.machineTypes], sections: [...hit.sections], departments: hit.departments ? [...hit.departments] : null }
    : { machineTypes: null, sections: null, departments: null };
}

export function sectionMatches(section: string | null | undefined, sections: string[]): boolean {
  if (!section) return false;
  const s = norm(section);
  return sections.some((x) => norm(x) === s);
}

/** Phòng sản xuất (dữ liệu có cả tên "Sản xuất" lẫn mã "PROD") */
export function isProductionDepartment(department: string | null | undefined): boolean {
  const d = norm(department);
  return d === 'sản xuất' || d === 'prod' || d === 'san xuat';
}

/** Tên phòng sản xuất để lọc trong truy vấn (so khớp không phân biệt hoa thường) */
export const PRODUCTION_DEPARTMENT_NAMES = ['Sản xuất', 'PROD'];

export function machineFitsProcessGroup(machine: { type?: string | null }, groupCode: string | null | undefined): boolean {
  const { machineTypes } = resourcesForProcessGroup(groupCode);
  if (!machineTypes) return true;
  return !!machine.type && machineTypes.includes(machine.type);
}

export function userFitsProcessGroup(user: { section?: string | null; department?: string | null }, groupCode: string | null | undefined): boolean {
  const { sections, departments } = resourcesForProcessGroup(groupCode);
  if (!sections) return true;
  return sectionMatches(user.section, sections) || (!!departments && sectionMatches(user.department, departments));
}
